/**
 * NAV FRESHNESS, IN INDIAN CIVIL DATES (RM-W1-11, ruling OR-03).
 *
 * A mutual fund NAV is published once per Indian business day and carries the civil date it belongs
 * to. Until this module existed the public fund surface printed the NAV and its date with no
 * qualifier, so a value that had not moved for three weeks looked exactly like one published this
 * morning. OR-03 settled the thresholds and settled that `/funds` stays up while the AMFI feed is
 * repaired, which makes the badge the thing that keeps the page honest:
 *
 *   fresh       ageDays <= 3
 *   stale       4 <= ageDays <= 14
 *   very_stale  ageDays >= 15
 *   invalid     null, unparseable, or a date in the future
 *
 * WHY CIVIL DATES AND NOT MILLISECONDS. `Date.now() - navDate` measures elapsed time, and elapsed
 * time is not what "how many days old is this NAV" means. Two instants 26 hours apart can be one
 * calendar day apart or two, depending on where the local midnights fall, and a daylight-saving
 * transition anywhere in the chain moves a 24-hour bucket by an hour. So both sides are reduced to a
 * civil date first, and the difference is taken with `Date.UTC` on those components, which is exact
 * integer arithmetic on a fixed 86,400,000 ms grid with no zone left in it:
 *
 *   ageDays = (Date.UTC(y2, m2, d2) - Date.UTC(y1, m1, d1)) / 86_400_000
 *
 * `now` is reduced to its civil date in `Asia/Kolkata`, because the question is which Indian day it
 * is, not which day it is where the reader or the server happens to be.
 *
 * WEEKENDS AND HOLIDAYS ARE NOT SPECIAL-CASED, on purpose. A Monday reading of Friday's NAV is three
 * days old and reports `fresh` because three days is the honest number, not because the weekend was
 * subtracted. Subtracting non-business days would mean the badge quietly encodes an Indian market
 * calendar that nothing else in this repository owns, and would report "fresh" through a long holiday
 * closure where the feed may equally have died. The tooltip states this rather than hiding it.
 *
 * THE FUTURE IS `invalid`, NEVER `fresh`. A NAV dated tomorrow is a data defect, and the failure
 * direction that matters is the one where a broken value renders as the most reassuring state.
 *
 * This module is PURE: no clock of its own, no environment reads, no I/O. The caller supplies `now`.
 */

/** The zone whose civil date defines "today" for a NAV. */
export const NAV_CIVIL_TIME_ZONE = "Asia/Kolkata";

/** OR-03: at or below this many days old, a NAV is `fresh`. */
export const NAV_FRESH_MAX_AGE_DAYS = 3;

/** OR-03: at or below this many days old (and above the fresh bound), a NAV is `stale`. */
export const NAV_STALE_MAX_AGE_DAYS = 14;

const MS_PER_DAY = 86_400_000;

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export type NavFreshnessLevel = "fresh" | "stale" | "very_stale" | "invalid";

/** Why a reading is `invalid`. `null` on every level other than `invalid`. */
export type NavInvalidReason = "missing" | "unparseable" | "future" | null;

/** A calendar date with no zone attached. `month` is 1 to 12. */
export type CivilDate = {
  year: number;
  month: number;
  day: number;
};

export type NavFreshnessReading = {
  level: NavFreshnessLevel;
  /** Whole civil days between the NAV date and today in `Asia/Kolkata`. `null` when invalid. */
  ageDays: number | null;
  /** The NAV's own civil date. `null` when invalid, including for a future date. */
  civilDate: CivilDate | null;
  reason: NavInvalidReason;
  /** "Aug 18, 2026", or "date unavailable". Never empty: a NAV is never printed without this. */
  dateLabel: string;
  /** "as of Aug 18, 2026", or "date unavailable". */
  asOfLabel: string;
  /** "Fresh" | "Stale" | "Very stale" | "Date unavailable". */
  badgeLabel: string;
  /** The info tooltip. States the threshold semantics, per the house rule on UI metrics. */
  tooltip: string;
  /** The OG cache version key: "2026-08-18", or "unknown". Safe in a URL query without escaping. */
  versionKey: string;
};

/** Reads the civil date of an instant in a named IANA zone. */
export function civilDateInZone(instant: Date, timeZone: string): CivilDate | null {
  if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  let year: number | null = null;
  let month: number | null = null;
  let day: number | null = null;
  for (const part of parts) {
    if (part.type === "year") year = Number(part.value);
    if (part.type === "month") month = Number(part.value);
    if (part.type === "day") day = Number(part.value);
  }
  if (year === null || month === null || day === null) return null;
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return { year, month, day };
}

/**
 * Reads the civil date OUT of a stored NAV date without ever letting a zone shift it.
 *
 * Two shapes are accepted, and nothing else:
 *
 *   - a `Date`, read by its UTC components. `MutualFund.navDate` is a Prisma `DateTime` that
 *     `parseAmfiDate` writes as midnight on the AMFI civil date, so the UTC calendar components ARE
 *     the civil date. Reading local components instead would move the date a day west of UTC.
 *   - a string beginning `YYYY-MM-DD`, read LITERALLY from those characters. This covers the JSON the
 *     fund report API serves (`2026-08-18T00:00:00.000Z`), and reading the characters rather than
 *     parsing means a browser in Los Angeles and a browser in Sydney see the same date.
 *
 * Anything else is `null` (rendered "date unavailable"), which is the fail-closed direction: an
 * unrecognised shape must never be guessed into a reassuring badge.
 */
export function navCivilDate(navDate: string | Date | null | undefined): CivilDate | null {
  if (navDate === null || navDate === undefined) return null;
  if (navDate instanceof Date) {
    if (Number.isNaN(navDate.getTime())) return null;
    return {
      year: navDate.getUTCFullYear(),
      month: navDate.getUTCMonth() + 1,
      day: navDate.getUTCDate(),
    };
  }
  if (typeof navDate !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(navDate.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  // Reject a date that does not exist in its month (2026-02-31), which the regex alone accepts.
  const roundTrip = new Date(Date.UTC(year, month - 1, day));
  if (
    roundTrip.getUTCFullYear() !== year ||
    roundTrip.getUTCMonth() + 1 !== month ||
    roundTrip.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/** Whole days from `from` to `to`, on the civil-date grid. Negative when `to` precedes `from`. */
export function civilDayDifference(from: CivilDate, to: CivilDate): number {
  return (
    (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) /
    MS_PER_DAY
  );
}

/** "Aug 18, 2026". Built from the civil components, so no zone can move it. */
export function formatCivilDate(date: CivilDate): string {
  const inRange = date.month >= 1 && date.month <= 12;
  const month = inRange ? MONTH_SHORT[date.month - 1] : String(date.month);
  return `${month} ${date.day}, ${date.year}`;
}

/** "2026-08-18". The OG cache version key, and the same characters the NAV was stored under. */
export function formatCivilDateIso(date: CivilDate): string {
  const month = String(date.month).padStart(2, "0");
  const day = String(date.day).padStart(2, "0");
  return `${date.year}-${month}-${day}`;
}

const THRESHOLD_SENTENCE =
  `Fresh is ${NAV_FRESH_MAX_AGE_DAYS} days old or less, stale is ${NAV_FRESH_MAX_AGE_DAYS + 1} to ` +
  `${NAV_STALE_MAX_AGE_DAYS} days, very stale is ${NAV_STALE_MAX_AGE_DAYS + 1} days or more, ` +
  `counted in Indian civil dates (Asia/Kolkata).`;

const WEEKEND_SENTENCE =
  "Weekends and Indian market holidays are not excluded, so a Monday reading of Friday's NAV counts " +
  "as 3 days and is still fresh.";

function ageSentence(ageDays: number): string {
  if (ageDays === 0) return "This NAV is dated today.";
  if (ageDays === 1) return "This NAV is 1 day old.";
  return `This NAV is ${ageDays} days old.`;
}

function invalidReading(
  reason: Exclude<NavInvalidReason, null>,
  leadOverride?: string,
): NavFreshnessReading {
  const lead =
    leadOverride ??
    (reason === "future"
      ? "This fund's NAV carries a date in the future, so its age cannot be judged."
      : "This fund has no usable NAV date, so its age cannot be judged.");
  return {
    level: "invalid",
    ageDays: null,
    civilDate: null,
    reason,
    dateLabel: "date unavailable",
    asOfLabel: "date unavailable",
    badgeLabel: "Date unavailable",
    tooltip: `${lead} ${THRESHOLD_SENTENCE}`,
    versionKey: "unknown",
  };
}

/**
 * The reading. `navDate` is the fund's stored NAV date; `now` is the instant to judge it against,
 * supplied by the caller so this function stays pure and testable at exact boundaries.
 */
export function navFreshness(
  navDate: string | Date | null | undefined,
  now: Date,
): NavFreshnessReading {
  if (navDate === null || navDate === undefined || navDate === "") {
    return invalidReading("missing");
  }

  const navCivil = navCivilDate(navDate);
  if (!navCivil) return invalidReading("unparseable");

  const todayCivil = civilDateInZone(now, NAV_CIVIL_TIME_ZONE);
  if (!todayCivil) {
    return invalidReading(
      "unparseable",
      "Today's Indian civil date could not be determined, so this NAV's age cannot be judged.",
    );
  }

  const ageDays = civilDayDifference(navCivil, todayCivil);
  if (ageDays < 0) return invalidReading("future");

  const level: Exclude<NavFreshnessLevel, "invalid"> =
    ageDays <= NAV_FRESH_MAX_AGE_DAYS
      ? "fresh"
      : ageDays <= NAV_STALE_MAX_AGE_DAYS
        ? "stale"
        : "very_stale";

  const badgeLabel = level === "fresh" ? "Fresh" : level === "stale" ? "Stale" : "Very stale";
  const dateLabel = formatCivilDate(navCivil);

  return {
    level,
    ageDays,
    civilDate: navCivil,
    reason: null,
    dateLabel,
    asOfLabel: `as of ${dateLabel}`,
    badgeLabel,
    tooltip: `${ageSentence(ageDays)} ${THRESHOLD_SENTENCE} ${WEEKEND_SENTENCE}`,
    versionKey: formatCivilDateIso(navCivil),
  };
}

/**
 * The OG cache version key for a fund's NAV date. The fund OG image prints the NAV, which moves, so
 * its URL has to move with it: `?nav=2026-08-18` changes the moment the NAV date does, and the image
 * and the date it claims can never drift apart in a CDN. Returns "unknown" for an unusable date, so
 * the parameter is always present and always URL-safe.
 */
export function navVersionKey(navDate: string | Date | null | undefined): string {
  const civil = navCivilDate(navDate);
  return civil ? formatCivilDateIso(civil) : "unknown";
}

/** The page banner copy for a very stale fund. Exported so the wording is pinned by a test. */
export function veryStaleBannerText(reading: NavFreshnessReading): string {
  const age = reading.ageDays === null ? "more than two weeks" : `${reading.ageDays} days`;
  return (
    `This fund's NAV is ${age} old, dated ${reading.dateLabel}. It has not been refreshed since, so ` +
    "the NAV and anything computed from it are out of date. Returns and scores on this page were " +
    "built from the data available when the report was generated."
  );
}
